import debounce from 'lodash/debounce';
import capitalize from 'lodash/capitalize';
import React from 'react';
import { injectIntl } from 'react-intl';
import { Field, reduxForm } from 'redux-form';

import SingleColumn from '$ContainersLegacy/SingleColumn';
import CheckField from '$Fields/CheckField/CheckField-css';
import DateFieldPolyfill from '$Fields/DateField/DateFieldPolyfill';
import PasswordCreationField from '$Fields/PasswordCreationField/PasswordCreationField-css';
import SelectField from '$Fields/SelectField/SelectField-css';
import TextField from '$Fields/TextField/TextField-css';
import { IntlLink, IntlMessage } from '$HVUI';
import Button from '$Molecules/Buttons/Button';
import FormButton from '$Molecules/Buttons/FormButton';
import { fieldExtraMatch } from '$Utils';
import { formatDisplayPhone, normalizePhone } from '$Validation/Normalizer';
import {
    isEmail,
    isPhone,
    isStrongPassword,
    isValidBirthday,
    matches,
    noNumericChars,
    notEmpty
} from '$Validation/i18n-validator';

const passwordMeterDescriptions = [
    <IntlMessage id="account-creation.very-weak" key={0} />,
    <IntlMessage id="account-creation.weak" key={1} />,
    <IntlMessage id="account-creation.okay" key={2} />,
    <IntlMessage id="account-creation.strong" key={3} />,
    <IntlMessage id="account-creation.very-strong" key={4} />
];

const matchEmails = matches({
    fieldLabel: (
        <IntlMessage id="account-creation.email" defaultMessage="email" />
    ),
    fieldName: 'email'
});

export class SignupForm extends React.Component {
    // capture legalFirstName in a debounced onChange instead of
    // directly from _values, so it's not a partial
    state = {
        legalFirstName:
            (this.props._values && this.props._values.first_name) || null
    };
    constructor(props) {
        super(props);
        const {
            company: { minimum_age }
        } = this.props;
        // Have to set the function outside of the render block; bind here so `this` is defined
        this.isValidBirthday = isValidBirthday(minimum_age || 13).bind(this);
    }
    render() {
        const { _values, handleSubmit, intl, invalid, submitting } = this.props;

        const { legalFirstName } = this.state;

        const showIsSMS = fieldExtraMatch(
            { field: 'phone_number_type', value: 'mobile' },
            _values
        );

        // If the checkbox is checked, let's show preferred name
        const showPreferredName = _values && _values.has_preferred_name;

        // Debounce so it's not distracting while you type,
        // but DOES change before you have to click the checkbox
        const setLegalName = debounce(e => {
            this.setState({ legalFirstName: e.target.value });
        }, 1300);

        return (
            <SingleColumn marginBottom={72} marginTop={48}>
                <form
                    className="signup-form-wrapper"
                    data-complete={!invalid && !submitting}
                    onSubmit={handleSubmit}
                >
                    <Field
                        autoComplete="off"
                        autoCorrect="off"
                        component={TextField}
                        data={{
                            label: (
                                <IntlMessage
                                    id="account-creation.legal-first-name"
                                    defaultMessage="Legal First Name"
                                />
                            )
                        }}
                        help_text={
                            <IntlMessage
                                id="account-creation.please-match-the-name-on-your-state-issued"
                                defaultMessage="Please match the name on your State-issued ID,  Passport and/or insurance card."
                            />
                        }
                        name="first_name"
                        normalize={capitalize}
                        onChange={setLegalName}
                        spellCheck="false"
                        validate={[notEmpty]}
                    />
                    <Field
                        component={CheckField}
                        data={{
                            label: (
                                <IntlMessage
                                    id="account-creation.i-would-like-to-be-called-by"
                                    defaultMessage="I would like to be called by a different name than {name}"
                                    values={{
                                        name: legalFirstName ? (
                                            legalFirstName
                                        ) : (
                                            <IntlMessage
                                                id="account-creation.my-legal-first-name"
                                                defaultMessage="my Legal First Name"
                                            />
                                        )
                                    }}
                                />
                            )
                        }}
                        id="preferred-check"
                        labelCase="lowercase"
                        name="has_preferred_name"
                        style={{ minHeight: '48px' }}
                    />
                    {showPreferredName && (
                        <Field
                            autoCapitalize="off"
                            autoComplete="off"
                            autoCorrect="off"
                            component={TextField}
                            data={{
                                label: (
                                    <IntlMessage
                                        id="account-creation.please-call-me"
                                        defaultMessage="My preferred name is:"
                                    />
                                )
                            }}
                            labelCase="lowercase"
                            name="preferred_name"
                            spellCheck="false"
                            validate={[notEmpty, noNumericChars]}
                        />
                    )}
                    <Field
                        autoComplete="off"
                        autoCorrect="off"
                        component={TextField}
                        data={{
                            label: (
                                <IntlMessage
                                    id="account-creation.legal-last-name"
                                    defaultMessage="Legal Last Name"
                                />
                            )
                        }}
                        name="last_name"
                        normalize={capitalize}
                        spellCheck="false"
                        validate={[notEmpty]}
                    />
                    <Field
                        name="birthdate"
                        data={{
                            label: (
                                <IntlMessage
                                    id="account-creation.birthdate"
                                    defaultMessage="Birthdate"
                                />
                            )
                        }}
                        component={DateFieldPolyfill}
                        validate={[notEmpty, this.isValidBirthday]}
                    />
                    <Field
                        autoComplete="off"
                        autoCorrect="off"
                        component={TextField}
                        data={{
                            label: (
                                <IntlMessage
                                    id="account-creation.email"
                                    defaultMessage="Email Address"
                                />
                            )
                        }}
                        inputMode="email"
                        name="email"
                        spellCheck="false"
                        type="email"
                        validate={[notEmpty, isEmail]}
                    />
                    <Field
                        autoComplete="off"
                        autoCorrect="off"
                        component={TextField}
                        data={{
                            label: (
                                <IntlMessage
                                    id="account-creation.confirm-email"
                                    defaultMessage="Confirm Email Address"
                                />
                            )
                        }}
                        inputMode="email"
                        name="email_confirm"
                        spellCheck="false"
                        type="email"
                        validate={[notEmpty, matchEmails]}
                    />
                    <Field
                        component={PasswordCreationField}
                        data={{
                            label: (
                                <IntlMessage
                                    id="account-creation.password"
                                    defaultMessage="Password"
                                />
                            )
                        }}
                        name="password1"
                        style={{ margin: '1em 0 2em 0' }}
                        type="password"
                        descriptions={passwordMeterDescriptions}
                        showHide={{
                            hide: (
                                <IntlMessage
                                    id="account-creation.hide"
                                    key={0}
                                />
                            ),
                            show: (
                                <IntlMessage
                                    id="account-creation.show"
                                    key={1}
                                />
                            )
                        }}
                        zxcvbnErrorText={[
                            <IntlMessage
                                id="all.we-cant-check-the-strength-of-your"
                                key={0}
                            />,
                            <IntlMessage
                                id="all.refresh-the-page-to-try-again"
                                key={1}
                            />
                        ]}
                        validate={[notEmpty]}
                    />
                    <Field
                        component={TextField}
                        data={{
                            label: (
                                <IntlMessage
                                    id="account-creation.phone-number"
                                    defaultMessage="Phone Number"
                                />
                            )
                        }}
                        format={formatDisplayPhone}
                        inputMode="tel"
                        name="phone_number"
                        normalize={normalizePhone}
                        type="tel"
                        validate={[notEmpty, isPhone]}
                    />
                    <Field
                        choices={[
                            [
                                'mobile',
                                intl.formatMessage({
                                    id: 'account-creation.mobile',
                                    defaultMessage: 'Mobile'
                                })
                            ],
                            [
                                'work',
                                intl.formatMessage({
                                    id: 'account-creation.work',
                                    defaultMessage: 'Work'
                                })
                            ],
                            [
                                'home',
                                intl.formatMessage({
                                    id: 'account-creation.home',
                                    defaultMessage: 'Home'
                                })
                            ]
                        ]}
                        component={SelectField}
                        data={{
                            label: (
                                <IntlMessage
                                    id="account-creation.phone-type"
                                    defaultMessage="Phone Type"
                                />
                            )
                        }}
                        name="phone_number_type"
                        validate={[notEmpty]}
                    />
                    {showIsSMS && (
                        <Field
                            component={CheckField}
                            data={{
                                label: (
                                    <IntlMessage
                                        id="account-creation.allow-my-clinic-to-send-me-secure"
                                        values={{
                                            twilioShortcode:
                                                window.twilio_shortcode
                                        }}
                                    />
                                )
                            }}
                            id="agreement-check"
                            labelCase="lowercase"
                            name="is_sms"
                        />
                    )}
                    <Field
                        component={CheckField}
                        data={{
                            label: (
                                <>
                                    <IntlMessage
                                        id="account-creation.by-creating-an-acount-you-agree-to"
                                        defaultMessage="I agree to Healthvana's"
                                    />{' '}
                                    <IntlLink
                                        target="_blank"
                                        id="account-creation.external-terms-of-service"
                                        defaultMessage="Terms of Service"
                                        href="https://healthvana.com/terms-of-service/"
                                    />{' '}
                                    <IntlMessage
                                        id="account-creation.and"
                                        defaultMessage="and"
                                    />{' '}
                                    <IntlLink
                                        target="_blank"
                                        id="account-creation.external-privacy-policy"
                                        defaultMessage="Privacy Policy"
                                        href="https://healthvana.com/privacy-policy/"
                                    />
                                    .
                                </>
                            )
                        }}
                        id="tos-and-pn-agreement-check"
                        labelCase="lowercase"
                        name="tos_and_pn"
                        required={true}
                        validate={[notEmpty]}
                    />
                    <FormButton
                        center
                        element="button"
                        text={
                            <IntlMessage
                                id="all.submit"
                                defaultMessage="Submit"
                            />
                        }
                        style={{ marginBlock: '2rem' }}
                        {...this.props}
                    />
                </form>
                <div style={{ marginTop: '1em' }}>
                    <Button
                        element="Link4"
                        kind="secondary"
                        style={{ color: 'var(--red100)' }}
                        text={
                            <IntlMessage
                                id="account-creation.already-have-a-healthvana-account-log-in"
                                defaultMessage="Already have a Healthvana account? Log in now."
                            />
                        }
                        to="/login"
                    />
                </div>
            </SingleColumn>
        );
    }
}

export default reduxForm({
    asyncValidate: isStrongPassword('password1'),
    form: 'SignupForm'
})(injectIntl(SignupForm));
